export const TELEGRAM_URL = 'https://t.me/cryptoxnfts';

export const FUNNEL_CTA_LABEL = 'Join Free Telegram';

export const FUNNEL_DISCLAIMER =
  'Educational content only. Not financial advice. Cryptoassets are high risk and can lose value.';

const DEFAULT_COMMUNITY_STATS = [
  {
    value: '45K+',
    label: 'Members',
  },
  {
    value: 'Free',
    label: 'Education',
  },
  {
    value: 'Daily',
    label: 'Market updates',
  },
  {
    value: 'No',
    label: 'Payment step',
  },
];

function withDefaults(variant) {
  return {
    communityStats: DEFAULT_COMMUNITY_STATS,
    finalEyebrow: 'Free Telegram group',
    finalHeading: 'Join free. Learn the setup. Decide with rules.',
    finalCopy:
      'Open the free Telegram group for education notes, market context, and reminders to slow down before acting.',
    ...variant,
  };
}

export const funnelVariants = {
  risk_plan: withDefaults({
    key: 'risk_plan',
    slug: 'risk-plan',
    zAd: 'risk_plan_v01',
    headline: 'Know where the idea fails before you enter.',
    eyebrow: 'Risk-first crypto education',
    intro:
      'A coin idea is incomplete until the reason, invalidation, risk, and position size are written down.',
    heroBullets: ['Reason before entry', 'Invalidation before action', 'Free Telegram education'],
    primaryLesson:
      'A coin can be interesting and still be incomplete. Write down the level or condition that proves the idea wrong before thinking about entry.',
    signalLabel: 'Invalidation map',
    accent: 'Failure point first',
    marketState: 'Idea incomplete',
    proofSteps: [
      ['01', 'Define reason'],
      ['02', 'Mark invalidation'],
      ['03', 'Size the risk'],
    ],
    filterEyebrow: 'Decision filter',
    filterHeading: 'If the failure point is unclear, the plan is not ready.',
    checkLabels: ['Reason', 'Invalidation', 'Size'],
    checks: [
      'What is the reason this market idea exists?',
      'What price or condition makes the idea wrong?',
      'What size keeps the decision objective if the idea fails?',
    ],
    previewEyebrow: 'Inside the group',
    previewHeading: 'The free notes start with context and invalidation.',
    previewIntro:
      'The group is designed to make the decision slower and clearer before any action is considered.',
    previewItems: [
      'Context note: what the market is doing now',
      'Risk note: where the idea stops making sense',
      'Reminder: no single coin replaces a written plan',
    ],
    processEyebrow: 'Education loop',
    processHeading: 'A simple plan beats a fast reaction.',
    sections: [
      {
        title: 'Start with the downside',
        copy:
          'Most rushed decisions begin with a coin name. A stronger process begins with the amount at risk and the condition that cancels the idea.',
      },
      {
        title: 'Separate attention from action',
        copy:
          'A coin can be worth watching without being worth entering. The plan tells you when interest becomes action.',
      },
      {
        title: 'Review the decision',
        copy:
          'After the move, compare what happened with the original reason for entry. That is how the process improves.',
      },
    ],
  }),

  fomo_after_pump: withDefaults({
    key: 'fomo_after_pump',
    slug: 'fomo-after-pump',
    zAd: 'fomo_after_pump_v01',
    headline: 'A late entry needs a stricter filter.',
    eyebrow: 'Post-move crypto education',
    intro:
      'Fast candles create urgency. The free group teaches a slower check: structure, invalidation, and risk before action.',
    heroBullets: ['For post-pump decisions', 'No chasing reminders', 'Free Telegram education'],
    primaryLesson:
      'After a sharp move, the question is not whether the candle looks strong. The question is whether the setup is still measurable.',
    signalLabel: 'Late-entry filter',
    accent: 'Structure before speed',
    marketState: 'Move extended',
    proofSteps: [
      ['01', 'Pause the click'],
      ['02', 'Check the level'],
      ['03', 'Skip unclear risk'],
    ],
    filterEyebrow: 'FOMO filter',
    filterHeading: 'Do not let a green candle write the plan.',
    checkLabels: ['Extension', 'Level', 'Risk'],
    checks: [
      'Has the move already travelled far from a clean level?',
      'Where would the idea be invalidated if the move reverses?',
      'Would the decision still make sense if the candle was no longer green?',
    ],
    previewEyebrow: 'Inside the group',
    previewHeading: 'Get reminders that slow down post-move decisions.',
    previewIntro:
      'The free Telegram group is positioned as a check against urgency, not a place to chase candles.',
    previewItems: [
      'Late-entry reminder: what needs to be true before action',
      'Structure note: where the market would need to settle',
      'Risk note: when skipping is the cleaner decision',
    ],
    processEyebrow: 'Education loop',
    processHeading: 'Urgency is filtered before it becomes a click.',
    sections: [
      {
        title: 'Urgency is not confirmation',
        copy:
          'A fast candle can show attention, but it does not define a plan. The level still has to be measurable.',
      },
      {
        title: 'Late entries need rules',
        copy:
          'When the market has already moved, vague decisions become more expensive. The filter should get tighter.',
      },
      {
        title: 'Skipping is still a decision',
        copy:
          'If the level is unclear, staying out keeps attention and capital available for a cleaner setup.',
      },
    ],
  }),

  three_check_filter: withDefaults({
    key: 'three_check_filter',
    slug: 'three-check-filter',
    zAd: 'three_check_filter_v01',
    headline: 'Altcoins need context first.',
    eyebrow: 'Context-first crypto education',
    intro:
      'Before looking at an altcoin setup, check the broader market, the level, and the risk. The free group keeps that order clear.',
    heroBullets: ['Altcoin context first', 'Three-check framework', 'Free Telegram education'],
    primaryLesson:
      'An altcoin chart can look clean in isolation and still be weak if the broader context is against it.',
    signalLabel: '3-check review',
    accent: 'Context, level, risk',
    marketState: 'Checklist active',
    proofSteps: [
      ['01', 'Check context'],
      ['02', 'Check level'],
      ['03', 'Check risk'],
    ],
    filterEyebrow: 'Three-check filter',
    filterHeading: 'Do not read an altcoin chart in isolation.',
    checkLabels: ['Context', 'Level', 'Risk'],
    checks: [
      'What is the broader crypto market doing before this altcoin matters?',
      'Where is the level that makes the setup measurable?',
      'What is the risk if the setup fails quickly?',
    ],
    previewEyebrow: 'Inside the group',
    previewHeading: 'The free notes put context before the setup.',
    previewIntro:
      'The Telegram group gives reminders that stop one chart from becoming the whole decision.',
    previewItems: [
      'Context note: market backdrop before the altcoin',
      'Setup note: level and invalidation before action',
      'Risk note: whether the idea is clear enough to consider',
    ],
    processEyebrow: 'Education loop',
    processHeading: 'The order matters: context, level, risk.',
    sections: [
      {
        title: 'Context first',
        copy:
          'The same chart can mean different things in different market conditions. Context prevents isolated decisions.',
      },
      {
        title: 'Then the level',
        copy:
          'A clean level gives the decision structure. Without it, risk is hard to define and review.',
      },
      {
        title: 'Then the size',
        copy:
          'Size follows the level. It should not be decided by excitement, urgency, or the size of the candle.',
      },
    ],
  }),

  anti_signal_copying: withDefaults({
    key: 'anti_signal_copying',
    slug: 'anti-signal-copying',
    zAd: 'anti_signal_copying_v01',
    headline: 'Understand the setup before you follow the call.',
    eyebrow: 'Anti-copying crypto education',
    intro:
      'Random calls remove the reason, invalidation, and risk from the decision. The free group teaches what to check before following anything.',
    heroBullets: ['No blind-copy framing', 'Reason before action', 'Free Telegram education'],
    primaryLesson:
      'A market call is only useful if you understand why it exists, where it fails, and whether the risk fits your own rules.',
    signalLabel: 'Setup review',
    accent: 'Know the reason',
    marketState: 'Context required',
    proofSteps: [
      ['01', 'Ask why'],
      ['02', 'Find failure point'],
      ['03', 'Own the decision'],
    ],
    filterEyebrow: 'Copying filter',
    filterHeading: 'Do not follow a call you cannot explain.',
    checkLabels: ['Reason', 'Invalidation', 'Fit'],
    checks: [
      'What is the actual reason behind the market idea?',
      'What would prove the idea wrong?',
      'Does the risk fit your own rules before anyone else acts?',
    ],
    previewEyebrow: 'Inside the group',
    previewHeading: 'The free notes focus on reason, risk, and context.',
    previewIntro:
      'The Telegram group is positioned against blind copying, especially in volatile markets.',
    previewItems: [
      'Reason note: what the setup depends on',
      'Risk note: where the idea should be questioned',
      'Decision note: education first, action remains yours',
    ],
    processEyebrow: 'Education loop',
    processHeading: 'Education always beats blind copying.',
    sections: [
      {
        title: 'Understand the reason',
        copy:
          'A short market message is only the start. The useful part is the context behind the idea.',
      },
      {
        title: 'Own the decision',
        copy:
          'Every click belongs to the person making it. Education helps you slow down and evaluate the setup.',
      },
      {
        title: 'Build repeatable judgment',
        copy:
          'The goal is not to react faster. The goal is to make decisions that can be reviewed and improved.',
      },
    ],
  }),

  generic_crypto_education: withDefaults({
    key: 'generic_crypto_education',
    slug: 'generic',
    zAd: 'generic_crypto_education_v01',
    headline: 'Free crypto education before the next decision.',
    eyebrow: 'Crypto education group',
    intro:
      'Join the free Telegram group for market context, risk reminders, and simple decision checks before acting.',
    heroBullets: ['For any market condition', 'Daily market context', 'Free Telegram education'],
    primaryLesson:
      'The goal is not to react faster. The goal is to understand the setup, the risk, and the reason before making your own decision.',
    signalLabel: 'Education review',
    accent: 'Context before action',
    marketState: 'Notes open',
    proofSteps: [
      ['01', 'Read context'],
      ['02', 'Run checks'],
      ['03', 'Join free'],
    ],
    filterEyebrow: 'Simple decision check',
    filterHeading: 'Learn the setup before reacting to the market.',
    checkLabels: ['Context', 'Setup', 'Risk'],
    checks: [
      'What is the market context right now?',
      'What does the setup need before it is worth attention?',
      'What risk would make the idea too unclear?',
    ],
    previewEyebrow: 'Inside the group',
    previewHeading: 'A free place for education notes and risk reminders.',
    previewIntro:
      'The group keeps the message simple: market context, education notes, and risk reminders before you make your own decision.',
    previewItems: [
      'Market context notes',
      'Risk-first reminders',
      'Education-first framing before decisions',
    ],
    processEyebrow: 'Education loop',
    processHeading: 'Context, checks, and a calmer decision process.',
    finalCopy:
      'Open the free Telegram group for daily market updates, education notes, and risk-first reminders. No payment is requested on this page, and no outcomes are promised.',
    sections: [
      {
        title: 'Read the context',
        copy:
          'Short notes help frame what the market is doing without turning the page into financial advice.',
      },
      {
        title: 'Run the checks',
        copy:
          'The checks focus on setup quality, invalidation, and risk so the decision is easier to review later.',
      },
      {
        title: 'Keep responsibility clear',
        copy:
          'The group can provide education and reminders. The decision remains yours.',
      },
    ],
  }),
};

const variantsBySlug = Object.values(funnelVariants).reduce((accumulator, variant) => {
  accumulator[variant.slug] = variant;
  return accumulator;
}, {});

const variantsByAd = Object.values(funnelVariants).reduce((accumulator, variant) => {
  accumulator[variant.zAd] = variant;
  return accumulator;
}, {});

export function getFunnelVariant(identifier) {
  if (!identifier) {
    return funnelVariants.generic_crypto_education;
  }

  const normalized = String(identifier).trim();
  return (
    funnelVariants[normalized] ||
    variantsBySlug[normalized] ||
    variantsByAd[normalized] ||
    funnelVariants.generic_crypto_education
  );
}

export function resolveFunnelVariant({ variantSlug, search }) {
  const params = new URLSearchParams(search || '');
  return getFunnelVariant(
    variantSlug ||
      params.get('funnel_variant') ||
      params.get('variant') ||
      params.get('z_ad')
  );
}

export const funnelRouteVariants = Object.values(funnelVariants);
